/**
 * L.1: Active selection store — the single source of truth for
 * which client/matter/case the user is currently looking at.
 *
 * Drives the chat session key, the Active Context Pane content,
 * and any future workspace tree highlighting. Opening a file in
 * WorkspaceFilePane does NOT change the selection (the file lives
 * *within* the selected case; see layout-plan §4.2).
 *
 * Persistence: sessionStorage. Selection survives refresh but a
 * new browser tab starts with no selection (per layout-plan §4.3).
 *
 * Bidirectional sync convention (layout-plan §4.4):
 *   - URL → store: handled by `<SelectionUrlSync>`.
 *   - Store → URL: callers mutating the store MUST also call
 *     `navigate()` to keep the URL in sync. The store itself does
 *     not trigger navigation (avoids feedback loops + keeps the
 *     Router dependency out of the store). The tree-click handlers
 *     in WorkspaceLayout are the canonical example of this pattern.
 *
 * See: ai-docs/lexide-layout-plan.md §4
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createTenantScopedStorage } from "@/lib/tenant-scoped-storage";

export type ActiveScope = "client" | "matter" | "case";

export interface ActiveSelection {
  scope: ActiveScope;
  /** NULLABLE (D-8, flat storage): `null` only in `case` scope when the active
   *  case is assignment-pending (unassigned). `client`/`matter` scopes always
   *  carry a client. Consumers render "Unassigned" on null, never assume set. */
  clientId: string | null;
  matterId: string | null;
  caseId: string | null;

  /**
   * DISPLAY ONLY — a cache, never an identity (breadcrumb redesign B-2,
   * `ai-docs/lexide-breadcrumb-case-summary-redesign-plan.md` §3.2).
   *
   * Written by the surface that made the selection, which already holds the
   * human name — nine of the thirteen selection callers do (the CASOS rows,
   * `useCaseSwitcher`, the two context-pane list widgets, both create-dialogs,
   * the two re-select paths). That makes the lawyer's normal path cost **zero**
   * fetches; `useScopeNames` resolves the gap for the four that genuinely hold
   * only ids (URL restore, the route resolver, the two dev probes).
   *
   * ⚠ **NEVER** feed these into the chat session key, a route, or any RPC
   * argument. They are mutable labels; `clientId`/`matterId`/`caseId` remain the
   * only identity. They ride `partialize` with the rest of `selection`, so a
   * refresh keeps the label — which is the point, since the id alone cannot be
   * rendered to a lawyer.
   */
  clientName?: string;
  matterName?: string;
  caseTitle?: string;
}

interface ActiveContextStore {
  selection: ActiveSelection | null;
  /** `displayName` is the OPTIONAL human label (B-2 §3.2) — pass it whenever the
   *  calling surface already holds it, which avoids a resolver fetch entirely. */
  selectClient: (clientId: string, displayName?: string) => void;
  selectMatter: (clientId: string, matterId: string, displayName?: string) => void;
  selectCase: (
    clientId: string | null,
    matterId: string | null,
    caseId: string,
    displayName?: string,
  ) => void;
  clear: () => void;
  // Derived selector (layout-plan §4.3). Session-key derivation moved
  // out of the store in L.6 — see `useDerivedChatSessionKey` — so this
  // store stays pure state without the redundant key-format logic.
  getPaneScope: () => ActiveScope | null;
}

// DEV-only test accessor (mirrors the `useWorkspaceFileStore` pattern).
// Tests can manipulate the selection via `window.__acsStore.getState().selectX(...)`
// without needing the URL sync to fire.
declare global {
  interface Window {
    __acsStore?: typeof useActiveContextStore;
  }
}

export const useActiveContextStore = create<ActiveContextStore>()(
  persist(
    (set, get) => ({
      selection: null,
      selectClient: (clientId, displayName) =>
        set({
          selection: {
            scope: "client",
            clientId,
            matterId: null,
            caseId: null,
            clientName: displayName,
          },
        }),
      selectMatter: (clientId, matterId, displayName) =>
        set({
          selection: {
            scope: "matter",
            clientId,
            matterId,
            caseId: null,
            // The client's name is NOT carried here: this call knows the matter
            // it selected, not the client's label. `useScopeNames` resolves it.
            matterName: displayName,
          },
        }),
      selectCase: (clientId, matterId, caseId, displayName) =>
        set({
          selection: {
            scope: "case",
            clientId,
            matterId,
            caseId,
            caseTitle: displayName,
          },
        }),
      clear: () => set({ selection: null }),

      getPaneScope: () => get().selection?.scope ?? null,
    }),
    {
      name: "lexide:active-context",
      storage: createJSONStorage(() => createTenantScopedStorage(() => sessionStorage)),
      partialize: (state) => ({ selection: state.selection }),
    },
  ),
);

/**
 * The data-layer effective scope: `"org"` (the per-user "Global"
 * workspace) when nothing is selected, `"case"` otherwise.
 *
 * CCM Wave 2 (org-pane handoff): this is the SAME "effective scope is
 * global" notion the clarity slice's `ContextIndicator` uses
 * (`effectiveScopeIsGlobal = onGlobalThread || selection == null`),
 * but route-independent — the data hooks (`useCaseWorkspaceTree`,
 * `useFileBytes`, …) have no `useLocation`, and the file pane/tree show
 * the org workspace precisely when there is no active case/client/matter
 * selection. Keeping it a single derived selector means case ↔ org
 * data-fetch branches never drift from a second definition.
 *
 * ⚠ Known divergence (intentional, flagged for the operator): because
 * this is selection-based and the `ContextIndicator` is *route-aware*,
 * the two disagree in ONE edge — on the global thread (`/chat`) with a
 * case still SELECTED (the "stale-selection" parked case): the indicator
 * reports global ("Global / General" + Resume), while the workspace
 * pane/tree here follow the SELECTION and show that case's workspace. The
 * pane answers "which workspace am I viewing" (the selection); the
 * indicator answers "which scope is my chat" (the route). If the product
 * wants them aligned, make this selector route-aware (thread a shared
 * effective-scope through both) — a UX decision, not done here.
 *
 * Note: a `client`/`matter` selection (no case) is NOT "org" — those
 * scopes don't have a workspace tree of their own today, but they are a
 * deliberate non-global selection, so we report `"case"`-family scope
 * and let the existing case hooks' id-presence gates disable the fetch.
 * Only a fully-null selection is the org workspace.
 */
export type EffectiveScope = "org" | "case";

/** Pure derivation of the data-layer effective scope from a selection.
 *  Exported (separate from the hook) so the org-branch decision in the
 *  workspace data hooks is unit-testable in the project's node env
 *  without a React mount. */
export function effectiveScopeOf(
  selection: ActiveSelection | null,
): EffectiveScope {
  return selection == null ? "org" : "case";
}

export function useEffectiveScope(): EffectiveScope {
  const selection = useActiveContextStore((s) => s.selection);
  return effectiveScopeOf(selection);
}

if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__acsStore", {
    value: useActiveContextStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
